import React from "react";
import { Star, Briefcase, Clock, DollarSign, CheckCircle, XCircle, User } from "lucide-react";

/**
 * HandymanRequestCard Component
 * 
 * Displays an individual handyman request/offer for a scheduled task.
 * Shows handyman details, offered price, estimated time, and action buttons.
 * Highlights the card if the handyman is selected/accepted.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.request - Request object with handyman details and offer info
 * @param {boolean} props.isSelected - Whether this handyman is selected/accepted
 * @param {Function} props.onAccept - Handler for accept button click
 * @param {Function} props.onReject - Handler for reject button click
 * @param {Function} props.onViewProfile - Handler to view handyman profile
 * @returns {JSX.Element} Handyman request card
 */
const HandymanRequestCard = ({
    request,
    isSelected = false,
    onAccept,
    onReject,
    onViewProfile,
    taskStatus = "pending_offers",
    onApproveWork,
    onMakePayment,
    onFinalComplete,
}) => {
    // Generate initials from handyman name for avatar fallback
    const getInitials = (name) => {
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);
    };

    // Format request date to relative time (e.g., "2 hours ago")
    const getRelativeTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInMs = now - date;
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInDays > 0) {
            return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
        } else if (diffInHours > 0) {
            return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
        } else {
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
            return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
        }
    };

    return (
        <div
            className={`rounded-xl border-2 p-4 transition-all duration-300 ${isSelected
                ? "bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-400 dark:border-green-600 shadow-lg ring-2 ring-green-300 dark:ring-green-700"
                : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-[#EFA938] hover:shadow-md"
                }`}
        >
            {/* Selected Badge (shown only when handyman is selected) */}
            {isSelected && (
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-green-200 dark:border-green-800">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <div className="flex-1">
                        <span className="text-sm font-bold text-green-700 dark:text-green-400">
                            Selected Handyman
                        </span>
                        {taskStatus === "in_progress" && (
                            <p className="text-xs text-green-600 dark:text-green-500 font-medium">Work submitted for approval</p>
                        )}
                        {taskStatus === "approved" && (
                            <p className="text-xs text-blue-600 dark:text-blue-500 font-medium">Waiting for your payment</p>
                        )}
                        {taskStatus === "payment_received" && (
                            <p className="text-xs text-emerald-600 dark:text-emerald-500 font-medium">Payment received! Please rate</p>
                        )}
                    </div>
                </div>
            )}

            {/* Handyman Profile Section */}
            <div className="flex items-start gap-3 mb-4">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                    {request.handyman.avatar ? (
                        <img
                            src={request.handyman.avatar}
                            alt={request.handyman.name}
                            className="h-14 w-14 rounded-full object-cover ring-2 ring-gray-200 dark:ring-slate-700"
                        />
                    ) : (
                        <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#EFA938] to-orange-400 flex items-center justify-center text-white font-bold text-lg ring-2 ring-gray-200 dark:ring-slate-700">
                            {getInitials(request.handyman.name)}
                        </div>
                    )}
                    {/* Online status indicator */}
                    <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-green-500 border-2 border-white dark:border-slate-800"></div>
                </div>

                {/* Handyman Info */}
                <div className="flex-1 min-w-0">
                    {/* Name */}
                    <h4
                        className="text-base font-bold text-gray-900 dark:text-white truncate cursor-pointer hover:text-[#EFA938] transition-colors"
                        onClick={onViewProfile}
                        title={request.handyman.name}
                    >
                        {request.handyman.name}
                    </h4>

                    {/* Rating */}
                    <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {request.handyman.rating}
                            </span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
                        {/* Completed Jobs */}
                        <div className="flex items-center gap-1">
                            <Briefcase className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                                {request.handyman.completedJobs} jobs
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Offer Details */}
            <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Offered Price */}
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gray-50 dark:bg-slate-700/50">
                    <DollarSign className="h-4 w-4 text-[#EFA938] flex-shrink-0" />
                    <div className="min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Offer</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                            PKR {request.offeredPrice?.toLocaleString()}
                        </p>
                    </div>
                </div>

                {/* Estimated Time */}
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gray-50 dark:bg-slate-700/50">
                    <Clock className="h-4 w-4 text-[#EFA938] flex-shrink-0" />
                    <div className="min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Time</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                            {request.estimatedTime}
                        </p>
                    </div>
                </div>
            </div>

            {/* Request Timestamp */}
            <div className="mb-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Requested {getRelativeTime(request.requestDate)}
                </p>
            </div>

            {/* Action Buttons (only show if not selected and status is pending) */}
            {!isSelected && request.requestStatus === "pending" && (
                <div className="flex gap-2">
                    {/* Accept Button */}
                    <button
                        onClick={() => onAccept && onAccept(request)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                    >
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-sm">Accept</span>
                    </button>

                    {/* Reject Button */}
                    <button
                        onClick={() => onReject && onReject(request)}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-slate-700 transition-all duration-200"
                    >
                        <XCircle className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* View Profile Button (always visible) */}
            <button
                onClick={onViewProfile}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors ${!isSelected && request.requestStatus === "pending" ? "mt-2" : ""
                    }`}
            >
                <User className="h-4 w-4" />
                View Profile
            </button>

            {/* Completion Actions for Selected Handyman */}
            {isSelected && (
                <div className="mt-4 space-y-2 border-t border-gray-100 dark:border-slate-700 pt-4">
                    {taskStatus === "in_progress" && (
                        <button
                            onClick={onApproveWork}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold hover:from-blue-600 hover:to-indigo-600 transition-all shadow-md hover:shadow-lg transform active:scale-95"
                        >
                            <CheckCircle className="h-5 w-5" />
                            Approve Work
                        </button>
                    )}
                    {taskStatus === "approved" && (
                        <button
                            onClick={onMakePayment}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-[#EFA938] to-orange-500 text-white font-bold hover:from-[#d78c1f] hover:to-orange-600 transition-all shadow-md hover:shadow-lg transform active:scale-95"
                        >
                            <DollarSign className="h-5 w-5" />
                            Pay Now
                        </button>
                    )}
                    {taskStatus === "payment_received" && (
                        <button
                            onClick={onFinalComplete}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold hover:from-green-600 hover:to-emerald-600 transition-all shadow-md hover:shadow-lg transform active:scale-95"
                        >
                            <Star className="h-5 w-5" />
                            Rate & Complete
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default HandymanRequestCard;
